import { template as template_fd9b2463e5f141cfb5666b64daa1f11a } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { hash } from '@ember/helper';
import classNames from 'volta/helpers/class-names';
import { t } from 'ember-intl';
import gt from 'ember-truth-helpers/helpers/gt';
import eq from 'ember-truth-helpers/helpers/eq';
import or from 'ember-truth-helpers/helpers/or';
import { array } from '@ember/helper';
import orElse from 'volta/helpers/or-else';
import BvCardHeader from 'volta/components/bv-card/header';
import BvCardFooter from 'volta/components/bv-card/footer';
import BvCardSection from 'volta/components/bv-card/section';
import BvBtnGroup from 'volta/components/bv-btn-group';
import BvBtn from 'volta/components/bv-btn';
import BvPopover from 'volta/components/bv-popover';
import BvMenu from 'volta/components/bv-menu';
import BvCardSubsection from 'volta/components/bv-card/subsection';
import type { IBvMenuItem } from 'volta/components/bv-menu';
import type { ComponentLike, ContentValue } from '@glint/template';
import type { BvBtnArgs } from '../bv-btn';
import type { BvCardFooterSignature } from './footer';
import type { BvCardHeaderSignature, IHeaderAction } from './header';
import type { BvCardSectionSignature } from './section';
import type { BvCardSubsectionSignature } from './subsection';
type ICardArgs = BvBtnArgs & IBvMenuItem;
interface ICardAction extends ICardArgs {
    content?: ContentValue;
    icon?: string;
}
export interface BvCardSignature {
    Args: {
        embedded?: boolean;
        headerActions?: (IHeaderAction | undefined)[];
        headerAdditionalContent?: ContentValue | ComponentLike;
        onLightBg?: boolean;
        primaryFooterAction?: ICardAction;
        secondaryFooterActions?: (ICardAction | undefined)[];
        sectioned?: boolean;
        subdued?: boolean;
        subtitle?: ContentValue | ComponentLike;
        title?: ContentValue;
    };
    Blocks: {
        default: [{
                Section: ComponentLike<BvCardSectionSignature>;
                Subsection: ComponentLike<BvCardSubsectionSignature>;
                Header: ComponentLike<BvCardHeaderSignature>;
                Footer: ComponentLike<BvCardFooterSignature>;
            }];
        headerContent: [];
    };
    Element: HTMLDivElement;
}
export default class BvCardComponent extends Component<BvCardSignature> {
    get menuItems() {
        const { secondaryFooterActions } = this.args;
        if (secondaryFooterActions?.length && secondaryFooterActions.length > 1) {
            return secondaryFooterActions;
        }
        return [];
    }
    static{
        template_fd9b2463e5f141cfb5666b64daa1f11a(`
    {{#let
      (hash
        Section=(component BvCardSection)
        Subsection=(component BvCardSubsection)
        Header=(component BvCardHeader)
        Footer=(component BvCardFooter)
      )
      as |cardComponents|
    }}
      <div
        class={{classNames
          'bv-card'
          (if @subdued 'bv-card--subdued')
          (if @onLightBg 'bv-card--on-light')
          (if @embedded 'bv-card--embedded')
        }}
        ...attributes
      >
        {{#if @title}}
          <BvCardHeader
            @actions={{orElse @headerActions (array)}}
            @title={{@title}}
            @subtitle={{@subtitle}}
          >
            {{#if (has-block 'headerContent')}}
              {{yield to='headerContent'}}
            {{else}}
              {{! @glint-expect-error: Rendering ComponentLike in a block is not supported yet }}
              {{@headerAdditionalContent}}
            {{/if}}
          </BvCardHeader>
        {{/if}}
        {{#if @sectioned}}
          <BvCardSection>
            {{yield cardComponents}}
          </BvCardSection>
        {{else}}
          {{yield cardComponents}}
        {{/if}}
        {{#if (or @primaryFooterAction @secondaryFooterActions)}}
          <BvCardFooter>
            <BvBtnGroup @segmented={{false}} as |group|>
              {{#if (eq @secondaryFooterActions.length 1)}}
                {{#each @secondaryFooterActions as |_single|}}
                  <BvBtn
                    @iconLeft={{_single.icon}}
                    @onClick={{_single.onClick}}
                    @disabled={{_single.disabled}}
                  >
                    {{_single.content}}
                  </BvBtn>
                {{/each}}
              {{else if (gt @secondaryFooterActions.length 1)}}
                <BvPopover as |popover|>
                  <popover.Trigger class='u-mr-2'>
                    <BvBtn @disclosure={{true}} @text={{t 'more'}} />
                  </popover.Trigger>
                  <popover.Content>
                    <BvMenu @items={{this.menuItems}} @onAnyItemAction={{popover.close}} />
                  </popover.Content>
                </BvPopover>
              {{/if}}
              {{#if @primaryFooterAction}}
                <group.btn
                  @color='primary'
                  @testName={{@primaryFooterAction.testName}}
                  @text={{@primaryFooterAction.text}}
                  @iconLeft={{@primaryFooterAction.iconLeft}}
                  @iconRight={{@primaryFooterAction.iconRight}}
                  @onClick={{@primaryFooterAction.onClick}}
                  @disabled={{@primaryFooterAction.disabled}}
                  @isLoading={{@primaryFooterAction.isLoading}}
                />
              {{/if}}
            </BvBtnGroup>
          </BvCardFooter>
        {{/if}}
      </div>
    {{/let}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
declare module '@glint/environment-ember-loose/registry' {
    export default interface Registry {
        BvCard: typeof BvCardComponent;
        'bv-card': typeof BvCardComponent;
    }
}
